import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Clock, Users, Calendar, UserPlus, Check, Share2 } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

import { LOGO_URL } from "@/components/Navbar";

export default function JoinBooking() {
  const { bookingId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${bookingId}/share`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [bookingId]);

  const handleJoin = async () => {
    if (!user) { navigate("/login"); return; }
    setJoining(true);
    try {
      await api.post(`/bookings/${bookingId}/join`);
      toast.success(t("join.joined"));
      setJoined(true);
      // Refresh data
      const r = await api.get(`/bookings/${bookingId}/share`);
      setData(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return <div className="min-h-[70vh] flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-coral rounded-full border-t-transparent" /></div>;
  }

  if (!data) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <Card className="max-w-md w-full border-border/50">
          <CardContent className="p-8 text-center">
            <p className="text-lg font-heading font-bold mb-2">{t("join.not_found")}</p>
            <p className="text-sm text-muted-foreground mb-4">{t("join.expired")}</p>
            <Link to="/book"><Button className="rounded-full bg-coral hover:bg-coral-600 text-white">{t("join.book")}</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const alreadyJoined = joined || (user && data.players?.includes(user.name));
  const isFull = data.spots_left <= 0;

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg">
        {/* Invite Card */}
        <Card className="border-border/50 overflow-hidden" data-testid="join-booking-card">
          {/* Hero Banner */}
          {data.club_image && (
            <div className="relative h-40 overflow-hidden">
              <img src={data.club_image} alt={data.club_name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent" />
              <div className="absolute top-3 left-3">
                <Badge className="bg-teal/90 text-white border-0 rounded-full text-xs gap-1">
                  <Share2 size={10} /> {t("join.invite")}
                </Badge>
              </div>
            </div>
          )}

          <CardContent className="p-6 space-y-5">
            {/* Header */}
            <div className="text-center">
              <img src={LOGO_URL} alt="D&D Padel" className="h-8 w-8 mx-auto mb-3 rounded-lg" />
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1">{t("join.invited")}</p>
              <h1 className="font-heading font-black text-2xl">{data.club_name}</h1>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-xl bg-muted/30 border border-border/30 text-center">
                <Calendar size={16} className="mx-auto mb-1 text-coral" />
                <p className="font-bold text-sm">{data.date}</p>
                <p className="text-[10px] text-muted-foreground">{t("join.date")}</p>
              </div>
              <div className="p-3 rounded-xl bg-muted/30 border border-border/30 text-center">
                <Clock size={16} className="mx-auto mb-1 text-teal" />
                <p className="font-bold text-sm">{data.start_time} – {data.end_time}</p>
                <p className="text-[10px] text-muted-foreground">{t("join.time")}</p>
              </div>
              <div className="p-3 rounded-xl bg-muted/30 border border-border/30 text-center">
                <MapPin size={16} className="mx-auto mb-1 text-golden" />
                <p className="font-bold text-sm">{data.court_name}</p>
                <p className="text-[10px] text-muted-foreground">{data.club_region}</p>
              </div>
              <div className="p-3 rounded-xl bg-muted/30 border border-border/30 text-center">
                <Users size={16} className="mx-auto mb-1 text-coral" />
                <p className="font-bold text-sm">{data.players?.length || 1}/4</p>
                <p className="text-[10px] text-muted-foreground">{t("join.spots", { n: data.spots_left })}</p>
              </div>
            </div>

            {/* Players */}
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-2">{t("join.players")}</p>
              <div className="flex items-center gap-2">
                {data.players?.map((name, i) => (
                  <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-teal/10 border border-teal/20">
                    <div className="w-5 h-5 rounded-full bg-teal/20 flex items-center justify-center text-[9px] font-bold text-teal">
                      {name[0]?.toUpperCase()}
                    </div>
                    <span className="text-xs font-medium">{name}</span>
                  </div>
                ))}
                {Array.from({ length: data.spots_left }).map((_, i) => (
                  <div key={`empty-${i}`} className="w-8 h-8 rounded-full border-2 border-dashed border-coral/30 flex items-center justify-center">
                    <UserPlus size={12} className="text-coral/50" />
                  </div>
                ))}
              </div>
            </div>

            {/* Price */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-coral/5 border border-coral/15">
              <span className="text-sm text-muted-foreground">{t("join.price")}</span>
              <span className="text-xl font-black text-coral">Rs {data.price_per_player}</span>
            </div>

            {/* CTA */}
            {alreadyJoined ? (
              <div className="text-center p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                <Check size={24} className="mx-auto mb-2 text-green-500" />
                <p className="font-heading font-bold text-green-500">{t("join.in")}</p>
                <p className="text-xs text-muted-foreground mt-1">{t("join.see_you")}</p>
              </div>
            ) : isFull ? (
              <div className="text-center p-4 rounded-xl bg-muted/30 border border-border/30">
                <p className="font-heading font-bold text-muted-foreground">{t("join.full")}</p>
                <p className="text-xs text-muted-foreground mt-1">{t("join.all_taken")}</p>
              </div>
            ) : (
              <Button
                data-testid="join-booking-btn"
                onClick={handleJoin}
                disabled={joining}
                className="w-full rounded-full bg-coral hover:bg-coral-600 text-white h-12 font-semibold text-base gap-2 shadow-lg shadow-coral/25"
              >
                {joining ? t("join.joining") : (
                  <><UserPlus size={18} /> {t("join.join")}</>
                )}
              </Button>
            )}

            {!user && !alreadyJoined && !isFull && (
              <p className="text-xs text-center text-muted-foreground">
                <Link to="/login" className="text-coral underline">{t("join.signin")}</Link> {t("join.or")} <Link to="/register" className="text-coral underline">{t("join.create")}</Link> {t("join.to_join")}
              </p>
            )}

            {/* Hosted by */}
            <p className="text-[10px] text-center text-muted-foreground">
              {t("join.hosted")} <span className="font-semibold">{data.booker_name}</span> {t("join.on")}
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
